import { useMemo, useState } from "react";
import type { Poll } from "@/types/content";
import { formatNumber } from "@/utils/format";

interface PollCardProps {
  poll: Poll;
  topicTitle?: string;
  onVote?: (optionIndex: number) => void;
}

const barColors = ["bg-primary-500", "bg-secondary-500", "bg-accent-500"];

export default function PollCard({ poll, topicTitle, onVote }: PollCardProps) {
  const [votedIndex, setVotedIndex] = useState<number | null>(null);

  const total = poll.totalVotes + (votedIndex === null ? 0 : 1);

  const results = useMemo(
    () =>
      poll.options.map((option, index) => ({
        label: option.label,
        votes: option.votes + (votedIndex === index ? 1 : 0),
      })),
    [poll.options, votedIndex]
  );

  const handleVote = (index: number) => {
    if (votedIndex !== null) return;
    setVotedIndex(index);
    onVote?.(index);
  };

  return (
    <section
      className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6"
      aria-label="投票结果展示"
    >
      <div className="mb-1 flex items-center gap-2">
        <span className="flex h-4 w-4 items-center justify-center text-accent-600">
          <i className="ri-bar-chart-grouped-line" aria-hidden="true" />
        </span>
        <span className="font-label text-xs tracking-[0.18em] text-accent-700 uppercase">
          投票结果展示
        </span>
      </div>
      <h3 className="font-heading text-lg font-bold leading-snug text-foreground-950 md:text-xl">
        {poll.question}
      </h3>
      {topicTitle && (
        <p className="mt-1 text-xs text-foreground-500">相关话题：{topicTitle}</p>
      )}

      <div className="mt-5 space-y-4">
        {results.map((option, index) => {
          const percent = total > 0 ? (option.votes / total) * 100 : 0;
          const isPicked = votedIndex === index;
          return (
            <div key={option.label}>
              <div className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => handleVote(index)}
                  disabled={votedIndex !== null}
                  className={`inline-flex items-center gap-2 text-left font-label text-sm transition-colors ${
                    votedIndex === null
                      ? "cursor-pointer text-foreground-800 hover:text-primary-600"
                      : isPicked
                        ? "text-primary-700"
                        : "cursor-default text-foreground-600"
                  }`}
                  aria-label={`投票给：${option.label}`}
                >
                  {votedIndex === null ? (
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-checkbox-blank-circle-line text-xs" aria-hidden="true" />
                    </span>
                  ) : isPicked ? (
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-checkbox-circle-fill text-xs" aria-hidden="true" />
                    </span>
                  ) : (
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-checkbox-blank-circle-line text-xs" aria-hidden="true" />
                    </span>
                  )}
                  {option.label}
                </button>
                <span className="shrink-0 font-label text-sm font-medium text-foreground-900">
                  {percent.toFixed(1)}%
                </span>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-background-300">
                <span
                  className={`block h-full rounded-full transition-[width] duration-700 ease-out ${
                    isPicked ? "bg-primary-600" : barColors[index % barColors.length]
                  }`}
                  style={{ width: `${percent}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-background-300 pt-4 font-label text-xs text-foreground-500">
        <span className="inline-flex items-center gap-1">
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-group-line" aria-hidden="true" />
          </span>
          {formatNumber(total)} 人参与
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-time-line" aria-hidden="true" />
          </span>
          {poll.closesAt} 截止
        </span>
        <span className="ml-auto inline-flex items-center gap-1 text-secondary-700">
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-shield-check-line" aria-hidden="true" />
          </span>
          一人一票 · 结果实时更新
        </span>
      </div>

      {votedIndex !== null && (
        <p className="mt-3 rounded-md bg-secondary-50 px-3 py-2 font-label text-xs text-secondary-800">
          已记录你的选择，下拉可查看网友评论里的观点交锋。
        </p>
      )}
    </section>
  );
}